import React, { Suspense } from 'react'
import { Route, Routes, Navigate } from 'react-router-dom'

// Eager: routing infrastructure + the unauthenticated landing screen (kept in
// the main bundle so the login page paints instantly). `Loader` must be eager —
// it is the <Suspense> fallback shown while a lazy chunk downloads.
import NotFound from '../screens/_404'
import AuthGuard from '../wrappers/auth-guard'
import Loader from '../shared/loader'
import Login from '../screens/login'
import ChunkErrorBoundary from '../shared/chunk-error-boundary'

// Code-split every authenticated screen so the initial bundle (and the login
// page) no longer pull in react-webcam, jsencrypt, crypto-browserify,
// react-datepicker, react-select, etc. Each screen's chunk downloads on first
// navigation; the <Suspense> fallback below covers the load.
const ForgetPassword = React.lazy(() => import('../screens/forget-password'))
const ResetPassword = React.lazy(() => import('../screens/reset-password'))
const Logout = React.lazy(() => import('../screens/logout'))
const Cash = React.lazy(() => import('../screens/cash'))
const Cheque = React.lazy(() => import('../screens/cheque'))
const CreditCard = React.lazy(() => import('../screens/credit-card'))
// Post-login landing screens (every role lands on one of these three). Prefetch
// them so their chunk is already cached by the time login completes — keeps the
// login -> landing transition instant instead of waiting on a chunk download.
const Dashboard = React.lazy(() => import(/* webpackPrefetch: true */ '../screens/dashboard'))
const HeadForm = React.lazy(() => import('../screens/heads/form'))
const Head = React.lazy(() => import('../screens/heads/index'))
const Invoice = React.lazy(() => import(/* webpackPrefetch: true */ '../screens/invoices/index'))
const InvoiceForm = React.lazy(() => import('../screens/invoices/form'))
const LineItemForm = React.lazy(() => import('../screens/line-items/form'))
const LineItem = React.lazy(() => import('../screens/line-items/index'))
const RevenueLineItem = React.lazy(() => import(/* webpackPrefetch: true */ '../screens/revenue-line-items'))
const CompanyForm = React.lazy(() => import('../screens/ministries/form'))
const Company = React.lazy(() => import('../screens/ministries/index'))
const PayeeForm = React.lazy(() => import('../screens/payee/new'))
const Payee = React.lazy(() => import('../screens/payee/index'))
const Payment = React.lazy(() => import('../screens/payment'))
const Profile = React.lazy(() => import('../screens/profile'))
const ReplayEmail = React.lazy(() => import('../screens/replay-email'))
const ReplaySms = React.lazy(() => import('../screens/replay-sms'))
const ReportForm = React.lazy(() => import('../screens/report/form'))
const Report = React.lazy(() => import('../screens/report/index'))
const Search = React.lazy(() => import('../screens/search/index'))
const SearchInvoice = React.lazy(() => import('../screens/search/invoices'))
const SearchTransaction = React.lazy(() => import('../screens/search/transactions'))
const Swipe = React.lazy(() => import('../screens/swipe'))
const TransactionForm = React.lazy(() => import('../screens/transactions/form'))
const Transaction = React.lazy(() => import('../screens/transactions/index'))
const UserForm = React.lazy(() => import('../screens/users/form'))
const Users = React.lazy(() => import('../screens/users/index'))
// const WalkIn = React.lazy(() => import('../screens/walk-ins'))
// const WalkInForm = React.lazy(() => import('../screens/walk-ins/form'))

/**
 * Files placed in src/screens will automatically be added as routes
 * Files/Folders that begin with an underscore (_) will be ignored
 * By defualt, the path would be `/${filename}` or `/${subfolder}/${filename}`
 * To set a different pathname, export ROUTE_PATHS from the file.
 * To enable multiple paths pointing to the same component,
 * ROUTE_PATHS is always an array
 * e.g:
 *    export const ROUTE_PATHS = ['/foobar', '/hello']
 *
 *    export default class Component extends React.Component {
 *      ....
 *    }
 *
 * By default, route components will be wrapped by AuthGuard and use exact matching
 * To not wrap a component, export ROUTE_OPEN as a truthy value
 * To not use exact matching, export ROUTE_EXACT as a falsey value
 * e.g:
 *    export const ROUTE_OPEN = true
 *    export const ROUTE_EXACT = false
 *
 *    export default class Component extends React.Component {
 *      ....
 *    }
 *
 */


const DecidePath = ({user}) => {
  const roles = user?.roles
  // const location = useLocation()

  if (roles) {
    return roles.find(r => ['revenue', 'treasury'].includes(r.name))
      ? <Navigate to='/invoices' />
      : <Navigate to='/dashboard' />
  }

  return <Navigate to='/login' />
}

const AppRouter = ({user}) => {
  // const [toggled, setToggled] = useState(false);
  // const navigate = useNavigate();

  // const allComponents = getScreenRoutes(true)

  // useEffect(() => {
  //   const roles = user?.roles
  //   console.log('roles', roles)
    
  //   if (roles) {
  //     return roles.find(r => ['revenue', 'treasury'].includes(r.name))
  //       ? navigate('/invoices')
  //       : navigate('/dashboard')
  //   } else {
  //     navigate('/login')
  //   }
  // }, [user, navigate])

  // const togglePanel = () => setToggled(!toggled);

  return (
    <ChunkErrorBoundary>
    <Suspense fallback={<Loader loading={true} />}>
      <Routes>
        <Route
          exact
          path='/'
          authenticated={!!user}
          element={<DecidePath user={user}/>}
        />

        {/* {allComponents} */}
        <Route element={<NotFound/>} />
        <Route 
          exact 
          path='/cash' 
          element={
            <AuthGuard user={user}>
              <Cash/>
            </AuthGuard>}
        />
        <Route exact path='/cheque' element={<AuthGuard user={user}><Cheque/></AuthGuard>}/>
        <Route exact path='/credit-card' element={<AuthGuard user={user}><CreditCard/></AuthGuard>}/>
        <Route exact path='/dashboard' element={<AuthGuard user={user}><Dashboard/></AuthGuard>}/>
        <Route exact path='/forget-password' element={<ForgetPassword/>}/>
        <Route exact path='/heads/form' element={<HeadForm/>}/>
        <Route exact path='/heads/new' element={<HeadForm/>}/>
        <Route exact path='/heads/edit' element={<HeadForm/>}/>
        <Route exact path='/heads' element={<Head/>}/>
        {/* <Route exact path='/invoices/form' element={<AuthGuard user={user}><InvoiceForm/></AuthGuard>}/> */}
        <Route exact path='/invoices/new' element={<AuthGuard user={user}><InvoiceForm/></AuthGuard>}/>
        <Route exact path='/invoices/edit' element={<AuthGuard user={user}><InvoiceForm/></AuthGuard>}/>
        <Route exact path='/invoices' element={<AuthGuard user={user}><Invoice/></AuthGuard>}/>
        {/* <Route exact path='/line-items/form' element={<AuthGuard user={user}><LineItemForm/></AuthGuard>}/> */}
        <Route exact path='/line-items/edit' element={<AuthGuard user={user}><LineItemForm/></AuthGuard>}/>
        <Route exact path='/line-items/new' element={<AuthGuard user={user}><LineItemForm/></AuthGuard>}/>
        <Route exact path='/line-items' element={<AuthGuard user={user}><LineItem/></AuthGuard>}/>
        <Route exact path='/login' element={<Login/>}/>
        <Route exact path='/logout' element={<Logout/>}/>
        {/* <Route exact path='/ministries/form' element={<AuthGuard user={user}><CompanyForm/></AuthGuard>}/> */}
        <Route exact path='/ministries/new' element={<AuthGuard user={user}><CompanyForm/></AuthGuard>}/>
        <Route exact path='/ministries/edit' element={<AuthGuard user={user}><CompanyForm/></AuthGuard>}/>
        <Route exact path='/ministries' element={<AuthGuard user={user}><Company/></AuthGuard>}/>
        <Route exact path='/payee' element={<AuthGuard user={user}><Payee/></AuthGuard>}/>
        <Route exact path='/payee/new' element={<AuthGuard user={user}><PayeeForm/></AuthGuard>}/>
        <Route exact path='/payment' element={<AuthGuard user={user}><Payment/></AuthGuard>}/>
        <Route exact path='/profile' element={<AuthGuard user={user}><Profile/></AuthGuard>}/>
        <Route exact path='/replay-email' element={<AuthGuard user={user}><ReplayEmail/></AuthGuard>}/>
        <Route exact path='/replay-sms' element={<AuthGuard user={user}><ReplaySms/></AuthGuard>}/>
        <Route exact path='/report/form' element={<AuthGuard user={user}><ReportForm/></AuthGuard>}/>
        <Route exact path='/report/generate' element={<AuthGuard user={user}><ReportForm/></AuthGuard>}/>
        <Route exact path='/report' element={<AuthGuard user={user}><Report/></AuthGuard>}/>
        <Route exact path='/reset-password/:token' element={<ResetPassword/>}/>
        <Route exact path='/revenue-line-items' element={<AuthGuard user={user}><RevenueLineItem/></AuthGuard>}/>
        <Route exact path='/search' element={<AuthGuard user={user}><Search/></AuthGuard>}/>
        <Route exact path='/search/invoices' element={<AuthGuard user={user}><SearchInvoice/></AuthGuard>}/>
        <Route exact path='/search/transactions' element={<AuthGuard user={user}><SearchTransaction/></AuthGuard>}/>
        <Route exact path='/swipe' element={<AuthGuard user={user}><Swipe/></AuthGuard>}/>
        {/* <Route exact path='/transactions/form' element={<AuthGuard user={user}><TransactionForm/></AuthGuard>}/> */}
        <Route exact path='/transactions/new' element={<AuthGuard user={user}><TransactionForm/></AuthGuard>}/>
        <Route exact path='/transactions/edit' element={<AuthGuard user={user}><TransactionForm/></AuthGuard>}/>
        <Route exact path='/transactions' element={<AuthGuard user={user}><Transaction/></AuthGuard>}/>
        {/* <Route exact path='/users/form' element={<AuthGuard user={user}><UserForm/></AuthGuard>}/> */}
        <Route exact path='/users/new' element={<AuthGuard user={user}><UserForm/></AuthGuard>}/>
        <Route exact path='/users/edit' element={<AuthGuard user={user}><UserForm/></AuthGuard>}/>
        <Route exact path='/users' element={<AuthGuard user={user}><Users/></AuthGuard>}/>
        {/* Walk-ins */}
        {/* <Route exact path='/walk-ins' element={<AuthGuard user={user}><WalkIn/></AuthGuard>}/>
        <Route exact path='/walk-ins/new' element={<AuthGuard user={user}><WalkInForm/></AuthGuard>}/> */}
      </Routes>
    </Suspense>
    </ChunkErrorBoundary>
  )
}

// class AppRouter extends React.Component {
//   state = {
//     toggled: false,
//   }

//   shouldComponentUpdate = nextProps => {
//     const curr = this.props.location.pathname
//     const next = nextProps.location.pathname

//     if (curr == '/login' && ['/dashboard', '/invoices'].includes(next)) {
//       return false
//     }

//     return curr != next
//   }

//   togglePanel = () => this.setState({ toggled: !this.state.toggled })

//   render = () => (
//     <Suspense fallback={<Loader loading={true} />}>
//       <Routes>
//         <Route
//           exact
//           path='/'
//           authenticated={!!global.RG.currentUser}
//           element={() => {
//             const roles = global.RG.currentUser?.roles

//             if (roles) {
//               return roles.find(r => ['revenue', 'treasury'].includes(r.name))
//                 ? <Navigate to='/invoices' />
//                 : <Navigate to='/dashboard' />
//             }

//             return <Navigate to='/login' />
//           }}
//         />

//         {/* {getScreenRoutes(true)} */}
//         <Route exact path='/login' element={<Login/>}/>
//         <Route element={<NotFound/>} />
//       </Routes>
//     </Suspense>
//   )
// }

// const getScreenRoutes = () => {
//   const context = require.context('@screens', true, /\.js$/);
//   const contextKeys = context.keys();
//   const routeComponents = []

//   // console.log('CONTEXT', context);

//   contextKeys.forEach((k, index) => {
//     if (/\/_/.test(k)) return index
//     // console.log('CONTEXT KEY', k);

//     const { default:def, ROUTE_PATHS, ROUTE_OPEN, ROUTE_EXACT } = context(k)
//     // console.log('CONTEXT FNC', context(k));


//     // if (ROUTE_PATHS) {
//     //   const adding = ROUTE_PATHS.map(path => React.createElement(Route, {
//     //     key: path,
//     //     path,
//     //     element,
//     //     exact: ROUTE_EXACT ? ROUTE_EXACT : true
//     //   }))
      
//     //   return adding
//     // }
    
//     const path = k
//       .replace(/\.js$/, '')
//       .replace(/^\.\//, '/')
//       .replace('/index', '')
    
//     let element = React.createElement(def, {key: path});
//     element = ROUTE_OPEN ? element : AuthGuard(element)
//     routeComponents.push(React.createElement(Route, {
//       key: path,
//       path,
//       element,
//       exact: ROUTE_EXACT ? ROUTE_EXACT : true
//     }))

//     // console.log('ROUTES', routeComponents, 'KEYS', k);
//   });
//   console.log('ROUTES', routeComponents);

//   return routeComponents;
// }

// const getScreenRoutesx = () => {
//   // const path = '/login';
//   // const element = <Login/>
//   // const loginRoute = React.createElement(Route, {
//   //   key: path,
//   //   path,
//   //   element,
//   //   exact: true
//   // })

//   // console.log(loginRoute)

//   // return loginRoute
//   const context = require.context('@screens', true, /\.js$/)
//   console.log('CONTEXT', context);
//   // const allRoutes = [];
  
//   return context.keys().reduce((routes, key) => {
//     // Ignore files files/folders that start with an underscore
//     if (/\/_/.test(key)) return routes
//     // if(key !== '/login') return routes + key;

//     const { default:def, ROUTE_PATHS, ROUTE_OPEN, ROUTE_EXACT } = context(key)
//     const element = ROUTE_OPEN ? def : AuthGuard(def)

//     if (ROUTE_PATHS) {
//       const adding = ROUTE_PATHS.map(path => React.createElement(Route, {
//         key: path,
//         path,
//         element,
//         exact: ROUTE_EXACT ? ROUTE_EXACT : true
//       }))
      
//       return [...routes, ...adding]
//     }
    
//     const path = key
//       .replace(/\.js$/, '')
//       .replace(/^\.\//, '/')
//       .replace('/index', '')
    
//       routes.push(React.createElement(Route, {
//       key: path,
//       path,
//       element,
//       exact: ROUTE_EXACT ? ROUTE_EXACT : true
//     }))

//     console.log('ROUTES', routes, 'KEYS', key);

//     return routes
//   }, [])
// }

// export default withRouter(AppRouter)
export default AppRouter
